import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
// Prevent unwanted focus styles
import '../listeners/focusStylesListener';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import devLogger from 'react-utils/devLogger';
import memoWithDisplayName from '../utils/memoWithDisplayName';
import validHref from '../utils/propTypes/validHref';
import { getSafeUrl } from '../utils/UnsafeUrl';
import { getRel, getTarget, warnIfShouldBeExternal, renderSecurityTaggedLink } from '../utils/Links';
import { getTabIndex } from '../utils/TabIndex';
import { UNDERLINE_CLASSES, USE_CLASSES } from './LinkConstants';
import UIIcon from '../icon/UIIcon';
import UITruncateString from '../text/UITruncateString';
import refObject from '../utils/propTypes/refObject'; // Hide icon in tables, except on hover (#5679)

const truncatedExternalIconMixin = css([".private-table tr:not(:hover) .private-link:not(:focus) &{visibility:hidden;}"]);
const DefaultExternalIcon = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, ["truncated"]);

  return /*#__PURE__*/_jsx(UIIcon, Object.assign({}, rest));
}).withConfig({
  displayName: "UILink__DefaultExternalIcon",
  componentId: "sc-1tq1i1x-0"
})(["", ""], ({
  truncated
}) => truncated && truncatedExternalIconMixin);
const UILink = memoWithDisplayName('UILink', props => {
  const {
    children,
    className,
    disabled = false,
    external = false,
    ExternalIcon = DefaultExternalIcon,
    href,
    iconProps,
    linkRef,
    onClick,
    onKeyDown,
    rel,
    role,
    tabIndex,
    target,
    // @ts-expect-error - Property 'to' does not exist on type 'UILinkProps & { children?: ReactNode; }'
    to,
    truncate = false,
    truncateStringProps,
    underline = false,
    use = 'dark'
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["children", "className", "disabled", "external", "ExternalIcon", "href", "iconProps", "linkRef", "onClick", "onKeyDown", "rel", "role", "tabIndex", "target", "to", "truncate", "truncateStringProps", "underline", "use"]);

  if (process.env.NODE_ENV !== 'production') {
    if (to != null) {
      devLogger.warn({
        message: `UILink: The \`to\` prop is a no-op. Did you mean \`href\`? (\`to="${to}"\`)`,
        key: 'UILink: to'
      });
    }
  }

  const defaultLinkRef = useRef(null);
  const computedTarget = getTarget(external, target);
  const computedRel = getRel(rel, computedTarget);
  const computedRole = href ? undefined : role || 'button';
  const externalLinkIcon = external ? /*#__PURE__*/_jsx(ExternalIcon, Object.assign({
    "aria-label": I18n.text('salesUI.UILink.externalTitle'),
    name: "externalLink"
  }, iconProps, {
    className: classNames('private-link--external__icon', iconProps && iconProps.className),
    role: "img",
    truncated: truncate
  })) : null;

  if (process.env.NODE_ENV !== 'production') {
    warnIfShouldBeExternal(external, computedTarget, 'UILink');
  }

  const wrappedChildren = truncate ? /*#__PURE__*/_jsx(UITruncateString, Object.assign({
    useFlex: true,
    fixedChildren: externalLinkIcon
  }, truncateStringProps, {
    children: children
  })) : children;

  const renderedLink = /*#__PURE__*/_jsxs("a", Object.assign({}, rest, {
    className: classNames('private-link', // @ts-expect-error - JS allows booleans as index types and will convert them to their string values "true" and "false"
    UNDERLINE_CLASSES[underline], className, use && USE_CLASSES[use], disabled && 'private-link--disabled'),
    onClick: evt => {
      if (disabled) {
        evt.preventDefault();
        evt.stopPropagation();
      } else {
        if (onClick) onClick(evt);
      }
    },
    onKeyDown: evt => {
      // Simulate click on Spacebar, to emulate button behavior
      const {
        key
      } = evt;
      if (onKeyDown) onKeyDown(evt);
      if (!onClick) return; // Ignore keydown events that bubbled up

      if (evt.target !== (linkRef || defaultLinkRef).current) return;

      if (key === ' ') {
        if (disabled) {
          evt.preventDefault();
          evt.stopPropagation();
          return;
        } // @ts-expect-error - KeyboardEvent is not the same as MouseEvent


        onClick(evt); // Prevent scrolling

        evt.preventDefault();
      }
    },
    href: disabled ? undefined : getSafeUrl(href),
    ref: linkRef || defaultLinkRef,
    rel: computedRel,
    role: computedRole,
    tabIndex: getTabIndex(disabled, tabIndex),
    target: computedTarget,
    "aria-disabled": disabled,
    children: [wrappedChildren, truncate ? null : externalLinkIcon]
  }));

  return renderSecurityTaggedLink(href, renderedLink);
});
UILink.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  external: PropTypes.bool,
  ExternalIcon: PropTypes.elementType,
  href: validHref,
  iconProps: PropTypes.object,
  linkRef: refObject,
  onKeyDown: PropTypes.func,
  rel: PropTypes.string,
  target: PropTypes.string,
  truncate: PropTypes.bool,
  truncateStringProps: PropTypes.object,
  underline: PropTypes.bool,
  use: PropTypes.oneOf(Object.keys(USE_CLASSES))
};
UILink.defaultProps = {
  external: false,
  ExternalIcon: DefaultExternalIcon,
  truncate: false,
  underline: false,
  use: 'dark',
  disabled: false
};
export default UILink;